import React, { createContext, useContext, useState } from 'react';
import { getJurisdiction, formatMoney as formatMoneyUtil, formatNumber as formatNumberUtil, type JurisdictionConfig, JURISDICTIONS } from '../utils/formatters';

interface LocalizationContextType {
  country: string;
  setCountry: (country: string) => void;
  currency: string;
  setCurrency: (currency: string) => void;
  jurisdiction: JurisdictionConfig;
  allJurisdictions: JurisdictionConfig[];
  formatMoney: (amount: number | undefined | null, overrideCurrency?: string) => string;
  formatNumber: (value: number | undefined | null) => string;
}

const LocalizationContext = createContext<LocalizationContextType | null>(null);

const STORAGE_KEY = 'hrms_active_jurisdiction';

export function LocalizationProvider({ children }: { children: React.ReactNode }) {
  const [country, setCountryState] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && JURISDICTIONS[saved] ? saved : 'US';
  });

  const [customCurrency, setCustomCurrency] = useState<string | null>(null);

  const setCountry = (newCountry: string) => {
    const code = (newCountry || 'US').toUpperCase();
    if (JURISDICTIONS[code]) {
      setCountryState(code);
      setCustomCurrency(null);
      localStorage.setItem(STORAGE_KEY, code);
    }
  };

  const jurisdiction = getJurisdiction(country);
  const activeCurrency = customCurrency || jurisdiction.currency;

  const formatMoney = (amount: number | undefined | null, overrideCurrency?: string) => {
    return formatMoneyUtil(amount, country, overrideCurrency || activeCurrency);
  };

  const formatNumber = (value: number | undefined | null) => {
    return formatNumberUtil(value, country);
  };

  const allJurisdictions = Object.values(JURISDICTIONS);

  return (
    <LocalizationContext.Provider
      value={{
        country,
        setCountry,
        currency: activeCurrency,
        setCurrency: setCustomCurrency,
        jurisdiction,
        allJurisdictions,
        formatMoney,
        formatNumber,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization(): LocalizationContextType {
  const ctx = useContext(LocalizationContext);
  if (!ctx) {
    // Fallback if rendered outside provider
    const fallbackJurisdiction = getJurisdiction('US');
    return {
      country: 'US',
      setCountry: () => {},
      currency: 'USD',
      setCurrency: () => {},
      jurisdiction: fallbackJurisdiction,
      allJurisdictions: Object.values(JURISDICTIONS),
      formatMoney: (amount) => formatMoneyUtil(amount, 'US'),
      formatNumber: (val) => formatNumberUtil(val, 'US'),
    };
  }
  return ctx;
}
